import React from "react";
import { Outlet, useLoaderData, Await, useRevalidator } from "react-router-dom";
import { useAuthContext } from "@src/context/authContext";
import OperatorContentProvider from "@src/context/operatorContext";
import {
  initUserActivityTracker,
  usePageViewTracker,
} from "@src/utils/userActivityTracker";
import OnboardingWrapper from "./onboarding/OnboardingWrapper";
import InactiveAccountWrapper from "./inactiveAccountWrapper";
import { Loader, Button } from "@src/components/atoms";
import GDPRReminder from "@src/components/cards/GdprReminderCard";
import SendgridCrewUpdateToast from "@src/components/cards/operator/SendgridCrewUpdateToast";
import { AppUtils } from "@src/utils";
import { queryClient } from "@src/utils/queryClient";

const MAX_BEHAVIOURS_PROVISIONING_RETRIES = 5;
const BEHAVIOURS_PROVISIONING_RETRY_DELAY_MS = 2000;

const DeactivatedAccountScreen = ({ securityLocked, onLogout }) => (
  <div className="flex-1 flex flex-col justify-center items-center text-center px-6 py-20">
    <div className="max-w-md">
      <p className="text-xl font-semibold text-gray-700 mb-2">
        This account has been locked
      </p>
      <p className="text-sm text-gray-500 mb-8">
        Please contact <a href="mailto:support@signol.io">support@signol.io</a>{" "}
        for further assistance.
      </p>
      <Button onClick={onLogout}>Log out</Button>
    </div>
  </div>
);

const OperatorSkeleton = ({ text = "Fetching your most recent data..." }) => (
  <div className="flex h-screen justify-center items-center">
    <Loader size="xl" text={text} textColor="black" />
  </div>
);

/**
 * Shown when operatorData resolves as null (loader ran before user cookie was ready).
 * Triggers a revalidation so the loader re-runs once auth has settled.
 */
const OperatorDataRevalidator = () => {
  const { revalidate, state } = useRevalidator();
  React.useEffect(() => {
    if (state === "idle") {
      revalidate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <OperatorSkeleton />;
};

/**
 * Shown when operatorData resolves but profile.behaviours is still empty —
 * behaviour provisioning for a newly-signed-up operator can lag behind the
 * auto-login redirect. Waits, then invalidates the cached OPERATOR_PROFILE
 * query (loader uses a 1hr staleTime, so a plain revalidate would just
 * return the same stale empty result) and revalidates the loader.
 *
 * Note: doesn't gate on useRevalidator().state — operatorRootLoader returns
 * synchronously (it wraps the profile fetch in a promise without awaiting
 * it, which is what lets <Await> show a skeleton without blocking
 * navigation), so `state` flips back to "idle" almost immediately after
 * revalidate() is called, well before the inner promise actually resolves.
 * Gating/depending on it here just resets this timer on that flicker.
 */
const OperatorBehavioursProvisioningWait = ({ onRetry }) => {
  React.useEffect(() => {
    const timer = setTimeout(onRetry, BEHAVIOURS_PROVISIONING_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [onRetry]);
  return <OperatorSkeleton text="Setting up your account..." />;
};

const BehavioursProvisioningFailedScreen = ({ onRetry, onLogout }) => (
  <div className="flex-1 flex flex-col justify-center items-center text-center px-6 py-20">
    <div className="max-w-md">
      <p className="text-xl font-semibold text-gray-700 mb-2">
        Your account is taking longer than expected to set up.
      </p>
      <p className="text-sm text-gray-500 mb-8">
        Please try again, or contact{" "}
        <a href="mailto:support@signol.io" className="underline">
          support@signol.io
        </a>{" "}
        if this persists.
      </p>
      <div className="flex gap-4 justify-center">
        <Button secondary block={false} onClick={onLogout}>
          Log out
        </Button>
        <Button block={false} onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  </div>
);

export const OperatorContainer = () => {
  const { initialData } = useLoaderData();
  const auth = useAuthContext();
  const { revalidate } = useRevalidator();
  const [provisioningAttempts, setProvisioningAttempts] = React.useState(0);

  const shouldTrack =
    auth.impersonatedSessionContext == null && !auth.hasAdminRole;
  initUserActivityTracker(auth, shouldTrack); // Start tracking globally
  usePageViewTracker(auth, shouldTrack); // Track route changes within this component

  const shouldShowGDPR =
    !auth?.impersonatedSessionContext && !AppUtils.isMockedApp;

  const retryBehavioursProvisioning = React.useCallback(() => {
    // loader's OPERATOR_PROFILE query has a 1hr staleTime — invalidate it
    // so the retry actually refetches instead of returning the same cached
    // (still-empty) behaviours. Safe to call revalidate() unconditionally —
    // see the note on OperatorBehavioursProvisioningWait for why gating on
    // useRevalidator().state here caused retries to get stuck.
    queryClient.invalidateQueries({ queryKey: ["OPERATOR_PROFILE"] });
    setProvisioningAttempts((n) => n + 1);
    revalidate();
  }, [revalidate]);

  // Manual retry from the failed screen — resets the counter so the user
  // gets a full new run of MAX_BEHAVIOURS_PROVISIONING_RETRIES attempts,
  // instead of incrementing past the cap and landing straight back on the
  // failed screen.
  const restartBehavioursProvisioning = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["OPERATOR_PROFILE"] });
    setProvisioningAttempts(0);
    revalidate();
  }, [revalidate]);

  return (
    <React.Suspense fallback={<OperatorSkeleton />}>
      <Await resolve={initialData}>
        {(operatorData) => {
          if (!operatorData) {
            // Loader resolved with null — user cookie wasn't ready in time.
            // Revalidate to re-run the loader now that auth has settled.
            return <OperatorDataRevalidator />;
          }
          if (operatorData.operator?.active === false) {
            return (
              <DeactivatedAccountScreen
                securityLocked={!!operatorData.operator.securityLocked}
                onLogout={auth.logout}
              />
            );
          }
          if (!operatorData.behaviours?.length) {
            // Newly signed-up operator — behaviour provisioning can lag
            // behind the auto-login redirect. OperatorContentProvider
            // renders nothing until behaviours are present, so surface a
            // loading/retry state here instead of leaving a blank page.
            if (provisioningAttempts >= MAX_BEHAVIOURS_PROVISIONING_RETRIES) {
              return (
                <BehavioursProvisioningFailedScreen
                  onRetry={restartBehavioursProvisioning}
                  onLogout={auth.logout}
                />
              );
            }
            return (
              // Keyed on attempt count: OperatorBehavioursProvisioningWait's
              // retry timer is armed in a mount effect, and this component
              // is otherwise just updated in place (not remounted) between
              // revalidations — a stable key would mean the effect's
              // [onRetry] deps never change after the first render, so the
              // timer would only ever fire once. The key forces a fresh
              // mount (and a fresh timer) on every attempt.
              <OperatorBehavioursProvisioningWait
                key={provisioningAttempts}
                onRetry={retryBehavioursProvisioning}
              />
            );
          }
          return (
            <OperatorContentProvider operatorData={operatorData}>
              <InactiveAccountWrapper />
              <OnboardingWrapper />
              <SendgridCrewUpdateToast />
              <Outlet />
              {shouldShowGDPR && <GDPRReminder />}
            </OperatorContentProvider>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
